import React, { useState, useEffect } from 'react';
import {
  Users, ArrowUpRight, Activity,
  Server, BarChart3, DollarSign, Cpu,
} from 'lucide-react';
import api from '../../services/api';
import { getSocket } from '../../services/socket';
import KPITile from '../../components/KPITile';

export default function AdminOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = () => api.get('/admin/dashboard').then(r => setData(r.data));
    load().finally(() => setLoading(false));

    const sock = getSocket();
    if (sock) sock.on('trade:opened', load);

    const interval = setInterval(load, 15000);
    return () => clearInterval(interval);
  }, []);

  if (loading) return <p style={{color:'var(--text-secondary)'}}>Loading dashboard…</p>;
  if (!data)   return <p style={{color:'var(--loss)'}}>Failed to load dashboard.</p>;

  const s = data.stats;
  const pnl = s.todayPnl ?? 0;
  const pnlIntent = pnl > 0 ? 'profit' : pnl < 0 ? 'loss' : 'neutral';
  const pnlTrend  = pnl > 0 ? 'up'     : pnl < 0 ? 'down' : 'flat';
  const fmt = n => Number(n || 0).toLocaleString();

  return (
    <>
      <div className="ct-page-header">
        <div>
          <span className="eyebrow">Admin</span>
          <h1>Overview</h1>
          <div className="subtitle">Live platform health and recent deployments</div>
        </div>
      </div>

      {/* KPI grid */}
      <div className="row">
        <div className="col-lg-3 col-md-6 mb-4">
          <KPITile label="Total Users" value={fmt(s.totalUsers)}
            sub={`${fmt(s.customers)} verified customers`}
            icon={<Users size={16} />} intent="neutral" />
        </div>
        <div className="col-lg-3 col-md-6 mb-4">
          <KPITile label="Running Containers" value={fmt(s.runningContainers)}
            sub="MT5 instances online" icon={<Server size={16} />} intent="neutral" />
        </div>
        <div className="col-lg-3 col-md-6 mb-4">
          <KPITile label="Active Deployments" value={fmt(s.activeDeployments)}
            sub={`${fmt(s.totalEAs)} EAs uploaded`}
            icon={<Activity size={16} />} intent="neutral" />
        </div>
        <div className="col-lg-3 col-md-6 mb-4">
          <KPITile label="Today P&L"
            value={`${pnl >= 0 ? '+' : ''}$${Number(pnl).toFixed(2)}`}
            sub={pnl >= 0 ? 'In profit' : 'In loss'}
            trend={pnlTrend}
            icon={<DollarSign size={16} />}
            intent={pnlIntent} />
        </div>
      </div>

      <div className="row">
        <div className="col-lg-8 mb-4">
          <div className="card">
            <div className="card-header" style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div>
                <h5 className="card-title">Recent Deployments</h5>
                <div className="card-category" style={{marginTop:4}}>Last 20 deployments</div>
              </div>
              <BarChart3 size={16} color="var(--text-tertiary)" />
            </div>
            <div className="card-body" style={{padding:0}}>
              {(data.recentActivity || []).length === 0 ? (
                <p style={{padding:'14px 20px', color:'var(--text-secondary)'}}>No deployments yet.</p>
              ) : (data.recentActivity || []).map(dep => (
                <div key={dep._id} style={{
                  display:'flex', justifyContent:'space-between', alignItems:'center',
                  padding:'12px 20px', borderBottom:'1px solid var(--border-subtle)',
                  fontSize:13, color:'var(--text-secondary)',
                }}>
                  <span>
                    <strong style={{color:'var(--text-primary)'}}>{dep.eaName}</strong>
                    {' '}on {dep.symbol} {dep.timeframe}
                    {' '}· {dep.userId?.username || dep.userId?.email}
                  </span>
                  <span className={`badge badge-${dep.status === 'deployed' ? 'success' : dep.status === 'error' ? 'danger' : 'secondary'}`}>
                    {dep.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="col-lg-4 mb-4">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title">Quick Stats</h5>
              <div className="card-category" style={{marginTop:4}}>Platform totals</div>
            </div>
            <div className="card-body" style={{padding:0}}>
              <QuickStat label="Verified Customers" value={fmt(s.customers)} icon={<ArrowUpRight size={14} />} />
              <QuickStat label="EAs Uploaded"       value={fmt(s.totalEAs)}  icon={<Cpu size={14} />} />
              <QuickStat label="Running"            value={fmt(s.runningContainers)} icon={<Server size={14} />} />
              <QuickStat label="Open Trades"        value={fmt(s.activeTrades)}
                icon={<Activity size={14} />} last />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function QuickStat({ label, value, icon, last }) {
  return (
    <div style={{
      display:'flex', justifyContent:'space-between', alignItems:'center',
      padding:'14px 20px',
      borderBottom: last ? 'none' : '1px solid var(--border-subtle)',
    }}>
      <span style={{display:'inline-flex', alignItems:'center', gap:10, color:'var(--text-secondary)', fontSize:13}}>
        <span style={{
          width:24, height:24, borderRadius:6, background:'var(--bg-surface-3)',
          display:'inline-flex', alignItems:'center', justifyContent:'center', color:'var(--text-secondary)',
        }}>{icon}</span>
        {label}
      </span>
      <span className="num" style={{fontWeight:600, fontSize:15, color:'var(--text-primary)'}}>{value}</span>
    </div>
  );
}
